// @ts-check
// Idempotency-Key generation, and the retry that is the only reason it exists.
//
// THE RULE: ONE KEY PER USER ACTION, GENERATED AT THE CLICK — not per attempt.
//
// A fresh key on every attempt is the default mistake and it defeats the whole
// mechanism: a request that timed out but actually succeeded server-side comes
// back as a second person, a second cohort, a second enrollment. Reusing one
// key across two different actions is the opposite mistake, and it is worse —
// the second request gets 409 idempotency_mismatch and can NEVER be made to
// succeed, because the key is already spent on a different body.
//
// Endpoints that honour it: POST /people, /cohorts, /cohorts/{id}/enrollments,
// .../enrollments/batch, /assessments/module, /assessments/capstone,
// /assessments/trainer, /check-codes. Keys expire after
// Academy:IdempotencyKeyTtlHours (24 by default) and are capped at 200 chars.

import { AcademyApiError, ErrorCodes, ClientErrorCodes } from './errors';

/** A key for ONE user action. Call it once, at the click, and reuse it. */
export function newIdempotencyKey() {
    // crypto.randomUUID needs a secure context. Dev runs over https (see
    // vite.config.js) so this is available; the fallback exists so a plain-http
    // preview does not silently lose idempotency.
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }
    return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Retries a request that carries an idempotency key.
 *
 * The key is generated ONCE, here, and passed to every attempt — which is the
 * whole point: attempt two is a replay of attempt one, and a replay returns the
 * original response rather than creating a second row.
 *
 * @template T
 * @param {(key: string) => Promise<T>} action  Receives the key; pass it as `idempotencyKey`.
 * @param {{ attempts?: number, baseDelayMs?: number }} [options]
 * @returns {Promise<T>}
 *
 * @example
 * await withIdempotency((key) =>
 *     post('/people', body, { idempotencyKey: key }));
 */
export async function withIdempotency(action, options = {}) {
    const attempts = options.attempts ?? 3;
    const baseDelayMs = options.baseDelayMs ?? 400;
    const key = newIdempotencyKey();

    let lastError;
    for (let attempt = 0; attempt < attempts; attempt++) {
        try {
            return await action(key);
        } catch (error) {
            lastError = error;

            // A client bug, not a transient failure. The key is spent on a
            // different body and no number of retries will change that, so stop
            // immediately rather than burning the whole schedule on it.
            if (error instanceof AcademyApiError
                && error.code === ErrorCodes.IdempotencyMismatch) {
                throw error;
            }

            if (!(error instanceof AcademyApiError) || !error.isRetryable) throw error;
            if (attempt === attempts - 1) break;

            // Fixed exponential schedule. The rate limiter sends no Retry-After
            // header, so there is nothing to honour and backing off blindly is
            // the documented behaviour.
            const delay = error.code === ClientErrorCodes.RateLimited
                ? baseDelayMs * 5 * (attempt + 1)
                : baseDelayMs * 2 ** attempt;
            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }
    throw lastError;
}
